import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Users, AlertTriangle, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { GeoThreat } from '../../types/disaster';
import { useDisasterFeed } from '../../hooks/useDisasterFeed';
import DisasterNewsModal from '../../../news/DisasterNewsModal';

const EnhancedDisasterFeed: React.FC = () => {
  // Get API key from Vite environment variables
  const apiKey = import.meta.env.VITE_NEWSDATA_API_KEY;
  const { disasters, loading, error, refetch } = useDisasterFeed(apiKey);
  
  // State for news modal
  const [isNewsModalOpen, setIsNewsModalOpen] = React.useState(false);
  const [selectedDisasterForModal, setSelectedDisasterForModal] = React.useState<GeoThreat | null>(null);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-accent-red border-accent-red/30 bg-accent-red/10';
      case 'high': return 'text-accent-orange border-accent-orange/30 bg-accent-orange/10';
      case 'medium': return 'text-accent-yellow border-accent-yellow/30 bg-accent-yellow/10';
      case 'low': return 'text-accent-green border-accent-green/30 bg-accent-green/10';
      default: return 'text-gray-400 border-gray-600/30 bg-gray-600/10';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-accent-red bg-accent-red/20';
      case 'monitoring': return 'text-accent-orange bg-accent-orange/20';
      case 'resolved': return 'text-accent-green bg-accent-green/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  const getCategoryEmoji = (category: string) => {
    const emojiMap: Record<string, string> = {
      natural_disaster: '🌍',
      human_health: '❤️',
      war_conflict: '⚔️',
      animal_health: '🐾',
      plant_agriculture: '🌱',
      environmental: '🌍',
      technological: '💻'
    };
    return emojiMap[category] || '⚠️';
  };

  const handleDisasterClick = (disaster: GeoThreat) => {
    setSelectedDisasterForModal(disaster);
    setIsNewsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsNewsModalOpen(false);
    setSelectedDisasterForModal(null);
  };

  // Show error if API key is missing
  if (!apiKey) {
    return (
      <div className="bg-dark-800/50 backdrop-blur-sm border border-accent-red/30 rounded-xl p-8 text-center">
        <AlertTriangle className="w-8 h-8 text-accent-red mx-auto mb-4" />
        <p className="text-accent-red">API key not found in environment variables</p>
        <p className="text-gray-400 text-sm mt-2">
          Please set VITE_NEWSDATA_API_KEY in your .env file
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-8 text-center">
        <div className="animate-spin inline-block w-8 h-8 border-4 border-primary-400 border-r-transparent rounded-full mb-4"></div>
        <p className="text-gray-400">Loading disaster data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-dark-800/50 backdrop-blur-sm border border-accent-red/30 rounded-xl p-8 text-center">
        <AlertTriangle className="w-8 h-8 text-accent-red mx-auto mb-4" />
        <p className="text-accent-red">{error}</p>
        <button 
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-accent-red/20 text-accent-red rounded-lg hover:bg-accent-red/30 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl overflow-hidden h-fit max-h-[600px]"
      >
        {/* Feed Header */}
        <div className="p-4 border-b border-dark-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-primary-400" />
            <h3 className="text-lg font-semibold text-white">Live Disaster Feed</h3>
            <span className="text-xs text-gray-400">({disasters.length} alerts)</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={refetch}
              className="p-1 hover:bg-dark-700 rounded transition-colors"
              title="Refresh feed"
            >
              <RefreshCw className="w-4 h-4 text-gray-400" />
            </button>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-accent-green rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-400">Live</span>
            </div>
          </div>
        </div>

        {/* Feed List */}
        <div className="overflow-y-auto max-h-[500px] scrollbar-thin scrollbar-thumb-dark-600 scrollbar-track-transparent">
          <div className="p-4 space-y-4">
            {disasters.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No disaster alerts at this time</p>
              </div>
            ) : (
              disasters.map((disaster, index) => (
                <motion.div
                  key={disaster.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => handleDisasterClick(disaster)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${getSeverityColor(disaster.severity)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getCategoryEmoji(disaster.category)}</span>
                      <div>
                        <h4 className="font-semibold capitalize text-white">
                          {disaster.type}
                        </h4>
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <MapPin className="w-3 h-3" />
                          <span>{disaster.location}</span>
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusColor(disaster.status)}`}>
                      {disaster.status}
                    </span>
                  </div>

                  <p className="text-sm text-gray-300 mb-3 line-clamp-2">
                    {disaster.description}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Users className="w-3 h-3" />
                        <span>{disaster.impactedCount.toLocaleString()} affected</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDistanceToNow(disaster.timestamp, { addSuffix: true })}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getSeverityColor(disaster.severity)}`}>
                      {disaster.severity}
                    </span>
                  </div>

                  {/* Progress indicator for active disasters */}
                  {disaster.status === 'active' && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Response Progress</span>
                        <span>Processing...</span>
                      </div>
                      <div className="w-full bg-dark-700 rounded-full h-1.5">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: '45%' }}
                          transition={{ duration: 1, delay: index * 0.1 + 0.5 }}
                          className="bg-gradient-to-r from-primary-400 to-primary-500 h-1.5 rounded-full"
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        </div>
      </motion.div>

      {/* News Modal */}
      <DisasterNewsModal
        isOpen={isNewsModalOpen}
        onClose={handleCloseModal}
        initialDisaster={selectedDisasterForModal}
      />
    </>
  );
};

export default EnhancedDisasterFeed;